import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { FileDown, Lock, MapPin, RadioTower, ScrollText, ShieldCheck } from "lucide-react";
import api, { downloadFile, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const CARD_STYLE = {
  expired: "border-l-red-500", expiring_30: "border-l-amber-500",
  expiring_60: "border-l-amber-400", expiring_90: "border-l-yellow-400",
};

export default function ComplianceConsole() {
  const [data, setData] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = useCallback(() => {
    api.get("/compliance/summary").then((r) => setData(r.data)).catch(() => {});
    api.get("/compliance/scheduler").then((r) => setScheduler(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setScanning(true);
    try {
      const { data: res } = await api.post("/documents/scan");
      toast.success(`Expiry scan complete — ${res.notifications_created} new notification(s)`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setScanning(false);
    }
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      await downloadFile("/compliance/report", "aetria-compliance-report.pdf");
      toast.success("Compliance Report downloaded");
    } catch {
      toast.error("PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  if (!data) return <Skeleton className="h-72 w-full rounded-none" />;
  const { summary, posture } = data;
  const c = summary.counts;

  return (
    <div className="space-y-5" data-testid="compliance-console">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-[13px] text-muted-foreground flex-1 min-w-[280px]">
          Daily scan 03:00 · weekly digest Mon 06:00 (Asia/Qatar).
          {scheduler?.jobs?.length ? ` Next scan: ${scheduler.jobs[0]?.next_run?.slice(0, 16).replace("T", " ")}.` : ""}
        </p>
        <Button onClick={runScan} disabled={scanning} variant="outline" data-testid="run-scan-btn"
          className="rounded-none h-9 text-[13px]">
          <RadioTower className="h-4 w-4" /> Run expiry scan
        </Button>
        <Button onClick={downloadReport} disabled={downloading} data-testid="compliance-report-btn"
          className="rounded-none h-9 text-[13px] bg-[var(--brand-primary)] hover:bg-slate-700 text-white">
          <FileDown className="h-4 w-4" /> Compliance Report (PDF)
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="risk-cards">
        {[["expired", "Expired"], ["expiring_30", "≤ 30 days"], ["expiring_60", "≤ 60 days"], ["expiring_90", "≤ 90 days"]].map(([k, label]) => (
          <div key={k} className={`bg-white border border-border border-l-[3px] px-4 py-3 ${CARD_STYLE[k]}`}>
            <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">{label}</p>
            <p className="text-2xl font-bold text-[var(--brand-primary)] mt-0.5">{c[k]}</p>
          </div>
        ))}
        <div className="bg-[var(--brand-primary)] text-white px-4 py-3" data-testid="overall-score-card">
          <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">Overall score</p>
          <p className="text-2xl font-bold mt-0.5">{summary.overall_score}%</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4 items-start">
        <div className="bg-white border border-border" data-testid="dept-scores">
          <p className="px-5 py-3 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold border-b border-border">
            Per-department compliance score
          </p>
          <div className="divide-y divide-border">
            {summary.departments.map((d) => (
              <div key={d.department} className="px-5 py-2.5 grid grid-cols-[140px_1fr_auto] items-center gap-3">
                <span className="text-[12.5px] font-medium text-slate-800">{d.department}</span>
                <div className="h-2 bg-slate-100">
                  <div className={`h-2 ${d.score >= 90 ? "bg-teal-600" : d.score >= 75 ? "bg-amber-500" : "bg-red-500"}`}
                    style={{ width: `${d.score}%` }} />
                </div>
                <span className="text-[11px] font-mono text-slate-600 w-20 text-right">
                  {d.score}% <span className="text-muted-foreground">· {d.total} docs</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-border" data-testid="posture-strip">
          <p className="px-5 py-3 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold border-b border-border">
            Qatar / QFC regulatory posture
          </p>
          <div className="divide-y divide-border text-[12px]">
            <div className="px-5 py-3 flex items-start gap-2.5">
              <Lock className="h-4 w-4 text-teal-700 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-slate-800">Data-at-rest encryption
                  <span className="ml-2 text-[9px] font-mono bg-teal-700 text-white px-1.5 py-0.5">ACTIVE</span></p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{posture.field_level_protected_records} document numbers field-level protected</p>
              </div>
            </div>
            <div className="px-5 py-3 flex items-start gap-2.5">
              <MapPin className="h-4 w-4 text-teal-700 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-slate-800">Data residency</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{posture.data_residency_region} — zone {posture.data_residency_zone}</p>
              </div>
            </div>
            <div className="px-5 py-3 flex items-start gap-2.5">
              <ShieldCheck className="h-4 w-4 text-teal-700 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-slate-800">AI gateway PII masking
                  <span className="ml-2 text-[9px] font-mono bg-teal-700 text-white px-1.5 py-0.5">ACTIVE</span></p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Raw PII never leaves the platform</p>
              </div>
            </div>
            <div className="px-5 py-3 flex items-start gap-2.5">
              <ScrollText className="h-4 w-4 text-teal-700 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-slate-800">Audit trail</p>
                <Link to="/security" className="text-[11px] text-teal-700 hover:text-teal-900 underline underline-offset-2" data-testid="audit-trail-link">
                  Open Security Console — gateway + PII reveal log
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
